import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import {
  Area, AreaChart, Bar, CartesianGrid, ComposedChart, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { getOpsLive } from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import ConversationDialog from "./ConversationDialog";
import {
  BLUE, GREEN, AMBER, RED, GRAY, NAVY,
  CounterCard, ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  StatusChip, identityColumns, drillColumn, useAutoRefresh,
  fmtInt, fmtDuration, fmtTime, safeNum,
} from "./opsShared";

const REFRESH_MS = 15000;

// ── THRESHOLDS ───────────────────────────────────────────────────────────────
// Derived from measured behaviour, not picked round. A healthy turn completes
// in ~7 seconds and the worker gives PromptFlow 60 before it gives up, so:
//
//   Delayed        any turn past 120s at 'Dispatched' — the worker is not there
//                  to time it out, which means the process died mid-call. There
//                  is no benign version of this, so the threshold is ZERO.
//   Longest wait   90s covers the 20s poll interval plus a slow turn with room
//                  to spare. Past 180s something is genuinely wedged.
//   Awaiting reply a backlog is normal during a campaign send; 50 is where it
//                  stops draining faster than it fills at CONCURRENCY 2.
const DELAYED_BAD      = 1;
const WAIT_WARN_SEC    = 90;
const WAIT_BAD_SEC     = 180;
const BACKLOG_WARN     = 50;
const BACKLOG_BAD      = 150;

function statusFor(value, warn, bad) {
  const v = safeNum(value);
  if (v >= bad) return "bad";
  if (v >= warn) return "warn";
  return "ok";
}

export default function AgentHealthDashboard() {
  // Opening a row pauses the auto-refresh: a table reshuffling under an open
  // dialog is disorienting, and the thread being read is a point-in-time
  // question anyway.
  const [drill, setDrill]     = useState(null);
  const [live, setLive]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [lastAt, setLastAt]   = useState(null);

  const { timeZone } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const load = useCallback(async () => {
    try {
      const d = await getOpsLive();
      setLive(d);
      setLastAt(new Date());
      setError("");
    } catch (e) {
      setError("Could not reach the monitoring service");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, REFRESH_MS, !drill);

  const snap = live?.snapshot || {};
  const delayed   = safeNum(snap.delayed);
  const waiting   = safeNum(snap.awaitingReply);
  const longest   = safeNum(snap.longestWaitSec);
  const inFlight  = safeNum(snap.inProgress) + safeNum(snap.awaitingAi);
  // Messages that will never be answered and are not a queue: replies that
  // arrived after the chat closed, opted-out numbers, rows already at the
  // delivery cap. Counted so the leak stays visible, kept out of the headline
  // so it does not read as a backlog.
  const unanswerable = safeNum(snap.unanswerable);

  const delayedStatus = delayed >= DELAYED_BAD ? "bad" : "ok";
  const waitStatus    = statusFor(longest, WAIT_WARN_SEC, WAIT_BAD_SEC);
  const backlogStatus = statusFor(waiting, BACKLOG_WARN, BACKLOG_BAD);

  const healthy = delayedStatus === "ok" && waitStatus === "ok" && backlogStatus !== "bad";

  const trend = (live?.trend || []).map((r) => ({
    time: fmtTime(r.minuteUtc, timeZone).replace(/^\d{2}\/\d{2}, /, ""),
    received: safeNum(r.received),
    answered: safeNum(r.answered),
    avgSecs:  r.avgSecs != null ? Math.round(safeNum(r.avgSecs)) : null,
  }));

  const openThread = (row) => setDrill(row);

  const delayedColumns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "customerMessage", label: "Customer's Message", width: 220 },
    { key: "stage", label: "Stage", width: 110,
      render: (r) => (
        <StatusChip
          label={r.stage === "Dispatched" ? "Waiting on AI" : "In progress"}
          tone={r.stage === "Dispatched" ? "bad" : "warn"}
        />
      ) },
    { key: "stuckSeconds", label: "Waiting", width: 90, align: "right",
      render: (r) => fmtDuration(r.stuckSeconds) },
    { key: "receivedUtc", label: "Received", width: 160,
      render: (r) => fmtTime(r.receivedUtc, timeZone) },
  ];

  return (
    <Box sx={{ p: 2 }}>
      {/* Status banner. The one thing readable from across the room. */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 2, mb: 2, px: 2, py: 1.25, borderRadius: 2,
        border: "1px solid",
        borderColor: healthy ? `${GREEN}55` : `${RED}55`,
        background: healthy ? `${GREEN}0D` : `${RED}0D`,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 10, height: 10, borderRadius: "50%",
            background: healthy ? GREEN : RED,
            boxShadow: `0 0 0 4px ${healthy ? GREEN : RED}22`,
          }} />
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: healthy ? GREEN : RED }}>
            {healthy ? "AI is responding normally" : "AI needs attention"}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          Refreshes every {REFRESH_MS / 1000}s · last checked{" "}
          {lastAt ? lastAt.toLocaleTimeString("en-US", { timeZone }) : "—"} {tzLabel}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !live && <LoadingRow text="Reading the pipeline…" />}

      {live && (
        <>
          <SectionTitle>Agent Health Right Now</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 1, mb: 2 }}>
            <CounterCard
              label="Awaiting Reply" value={fmtInt(waiting)} color={NAVY}
              status={backlogStatus} big
              sub={unanswerable > 0
                ? `Queued for AI · ${fmtInt(unanswerable)} more won't be answered`
                : "Queued for AI"}
            />
            <CounterCard
              label="Being Handled" value={fmtInt(inFlight)} color={BLUE} big
              sub="With AI right now"
            />
            <CounterCard
              label="Delayed Responses" value={fmtInt(delayed)} color={GREEN}
              status={delayedStatus} big
              sub={delayed > 0 ? "Past the expected window" : "None — all on time"}
            />
            <CounterCard
              label="Longest Wait" value={fmtDuration(longest)} color={GREEN}
              status={waitStatus} big
              sub="Oldest message still queued"
            />
            <CounterCard
              label="Answered Last Hour"
              value={fmtInt(trend.reduce((a, r) => a + r.answered, 0))}
              color={GREEN} big
              sub="Replies delivered"
            />
          </Box>

          {delayed > 0 && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
              {delayed} {delayed === 1 ? "message is" : "messages are"} past the expected
              response window. A turn stuck at "Waiting on AI" means the worker is no longer
              tracking it — check whether the service restarted. These customers may have
              been mid-payment.
            </Alert>
          )}

          <SectionTitle>Last 60 Minutes</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 1.5, mb: 1 }}>
            <ChartCard title="Messages received vs answered" height={240}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="received" name="Received" fill={NAVY} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="answered" name="Answered" fill={GREEN} radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Response time (sec)" height={240}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="liveSecs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BLUE} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={BLUE} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} tickFormatter={(v) => `${v}s`} />
                  <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [`${v} sec`, "Average"]} />
                  <Area type="monotone" dataKey="avgSecs" name="Average"
                    stroke={BLUE} fill="url(#liveSecs)" strokeWidth={2} connectNulls dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>
          <Caption>
            Blank stretches in the response-time line are minutes with no inbound messages,
            not missing data. Overnight the chart is mostly empty by design.
          </Caption>

          <SectionTitle right={`${(live.delayedRows || []).length} shown`}>
            Messages Needing Attention
          </SectionTitle>
          <DetailTable
            columns={delayedColumns}
            rows={live.delayedRows || []}
            emptyText="Nothing delayed — every message in the pipeline is within the expected window."
            maxHeight={360}
          />
          <Caption>
            A row here has been claimed but not completed. "Waiting on AI" is the serious
            one: the request reached AI and no answer came back, so a payment may have
            been attempted. Verify against the PNM before contacting the customer.
          </Caption>
        </>
      )}

      <ConversationDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        customerdataid={drill?.customerdataid}
        subtitle={drill?.stage ? `stuck at ${drill.stage}` : null}
      />
    </Box>
  );
}